import { quoteApi } from "@api";
import { BreadcrumbsCommon } from "@components";
import { IBreadcrumbs } from "@interfaces";
import { AppModuleLayout } from "@layouts";
import { TextField, useMediaQuery } from "@mui/material";
import { PATH_HOME, PATH_QUOTES, PATH_SHARE_QUOTES_WITH_EMAIL } from "@routes";
import { breakpoint, theme, validateShareQuoteForm, SHARE_NAME_MAX_LENGTH } from "@utilities";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

import styled from "styled-components";

const renderHeader = () => {
    return (
        <div className="heading-container">
            <div className="heading">
                <p>Share with friends and family</p>
            </div>
        </div>
    );
};

const Content = () => {
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const quoteId = searchParams.get("quoteId");

    const handleShare = async () => {
        const validation = validateShareQuoteForm({ email, firstName, lastName });
        if (!validation.valid) {
            ErrorToast({ Message: validation.message ?? "Invalid details" });
            return;
        }

        if (!quoteId) {
            ErrorToast({ Message: "Quote ID not found" });
            return;
        }

        setLoading(true);
        try {
            await quoteApi.ShareQuotesViaEmail({
                quoteId,
                shareToEmail: email.trim(),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
            });
            navigate(-1);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.title || "Failed to share quote";
            ErrorToast({ Message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ShareModal>
            <ShareModalContent>
                {!isMobile && <Title>Add Email</Title>}
                <ShareModalHeader>
                    <h3>Add Email address to send the payment link</h3>
                </ShareModalHeader>
                <TextField
                    label="Email"
                    type="email"
                    variant={isMobile ? "outlined" : "standard"}
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <NameFieldsRow>
                    <TextField
                        label="First name"
                        required
                        variant={isMobile ? "outlined" : "standard"}
                        fullWidth
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        inputProps={{ maxLength: SHARE_NAME_MAX_LENGTH }}
                    />
                    <TextField
                        label="Last name"
                        required
                        variant={isMobile ? "outlined" : "standard"}
                        fullWidth
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        inputProps={{ maxLength: SHARE_NAME_MAX_LENGTH }}
                    />
                </NameFieldsRow>
                <ShareContainer>
                    <ShareButton onClick={handleShare} disabled={loading}>
                        {loading ? "Sharing..." : "Share"}
                    </ShareButton>
                    <InfoText>
                        If they're not a Freelance user, they'll be invited to join and can get
                        points if they upgrade before booking.
                    </InfoText>
                </ShareContainer>
            </ShareModalContent>
        </ShareModal>
    );
};

export const ShareQuotesWithEmail = () => {
    const QuotesBreadcrumbs: IBreadcrumbs[] = [
        { title: "Home", link: PATH_HOME },
        { title: "Quotes", link: PATH_QUOTES },
        { title: "Share", link: PATH_SHARE_QUOTES_WITH_EMAIL },
    ];
    return (
        <AppModuleLayout>
            <TitleContainer>
                <div className="wrapper">
                    <BreadcrumbsWrapper>
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={QuotesBreadcrumbs} />
                        </div>
                    </BreadcrumbsWrapper>
                    <div className="subWrapper">
                        <div className="listCarts">
                            {renderHeader()}
                            <Content />
                        </div>
                    </div>
                </div>
            </TitleContainer>
        </AppModuleLayout>
    );
};

const TitleContainer = styled.div`
    padding: 16px;
    background-color: #f2f2f2;
    flex: 1;
    padding-bottom: 80px;

    ${breakpoint.breakOnlyMobile`
        background-color: #fff;
    `}

    .heading-container {
        display: flex;
        justify-content: space-between;
    }

    .wrapper {
        margin: 0 auto;

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}
    }
    .breadCrumbs {
        ${breakpoint.breakTablet`
            margin-top: 14px;
        `}
        ${breakpoint.breakOnlyMobile`
            left: 16px;
        `}
    }
    .subWrapper {
        ${breakpoint.breakTablet`
            display: flex;
            margin-top: 30px;
        `}
    }
    .listCarts {
        flex: 1;
        ${breakpoint.breakTablet`
            margin-top: 0;
        `}

        .button-heading {
            font-size: 16px;
            font-weight: 500;
            line-height: 120%;
            display: flex;
            align-items: center;
            font-family: poppins;
        }

        .heading {
            font-size: 20px;
            font-weight: 700;
            line-height: 36px;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 30px;

            ${breakpoint.breakIpadPro`
                margin-bottom: 25px;
            `}

            ${breakpoint.breakIpadPro`
                font-size: 28px;
            `}
        }
    }
`;

const BreadcrumbsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    .breadCrumbs {
        flex: 1;
    }
`;

const ShareModal = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const ShareModalContent = styled.div`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 700px;
    ${breakpoint.breakOnlyMobile`
        padding: 0px;
        height: 100%;
        flex: 1;
    `}
`;

const ShareModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 {
        font-size: 16px;
        font-weight: 500;
    }
    margin-bottom: 10px;
`;

const NameFieldsRow = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 16px;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
    `}
`;

const ShareContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        position: fixed;
        bottom: 20px;
        left: 20px;
        right: 20px;
        z-index: 1000;
    `}
`;

const ShareButton = styled.button`
    margin: 30px auto 0;
    width: 250px;
    align-items: center;
    padding: 12px 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    color: ${theme.colors.button_text};
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    display: block;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    ${breakpoint.breakOnlyMobile`
        width: 100%;
        margin: 0;
    `}
`;

const InfoText = styled.p`
    margin-top: 20px;
    font-size: 12px;
    color: #666;
    text-align: center;

    ${breakpoint.breakOnlyMobile`
        margin: 10px 0 0 0;
        padding: 0;
    `}
`;

const Title = styled.div`
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 20px;
`;
